import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import useSubmitData from "./useSubmitData";

// Hook containing all methods that need information about the video player
const useGameSession = (currentVideo) => {
    // State of the current score
    const [pointScoreState, setPointScoreState] = useState({
        scoreRedPlayer: 0,
        scoreBluePlayer: 0,
    });

    let penaltyObj = {};
    for (
        let i = 0;
        i < currentVideo.sessionProperties.sessionPenalties.length;
        i++
    ) {
        penaltyObj[currentVideo.sessionProperties.sessionPenalties[i].id] = 0;
    }
    const [penaltyRedScoreState, setPenaltyRedScoreState] = useState(penaltyObj);
    const [penaltyBlueScoreState, setPenaltyBlueScoreState] =
        useState(penaltyObj);

    const { checkRingOrTatami } = require("../../util/CheckRingOrTatami.js");

    const [activeStep, setActiveStep] = useState(0);

    const stepRef = useRef(0);
    const endTime = useRef(0);
    const overtimeRef = useRef(false);

    const playerDecisions = useRef([]);
    const videoPlayer = useRef(null);

    const [isOvertime, setIsOvertime] = useState(false);
    const [overtimeDuration, setOvertimeDuration] = useState(
        currentVideo.trailingPeriod
    );

    console.log(currentVideo.scoreDefinitions);
    const [overtimeStartTime, setOvertimeStartTime] = useState(0);

    const [videoEnd, setVideoEnd] = useState(false);
    const [videoStarted, setVideoStarted] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const videoEndRef = useRef(false);
    const [reviewEnd, setReviewEnd] = useState(false);

    const { savePlayerInputs, loadingFeedback, feedbackResult } = useSubmitData();

    const [videoProgress, setVideoProgress] = useState(0);
    const [activeDrawingScoreDefinition, setActiveDrawingScoreDefinition] =
        useState(null);

    const [videoHeight, setVideoHeight] = useState(0);
    const [videoWidth, setVideoWidth] = useState(0);
    const [canvasWidth, setCanvasWidth] = useState(0);
    const [canvasHeight, setCanvasHeight] = useState(0);
    const [canvasLeft, setCanvasLeft] = useState(0);
    const [canvasTop, setCanvasTop] = useState(0);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const [showBlurring, setShowBlurring] = useState(false);

    const handleChangeActiveDecision = (step) => {
        setActiveStep(step);
        stepRef.current = step;
    };

    // Handles what happens when you click a button on the stepper to replay a scored scene
    // in slow motion
    const handleReplayScene = (step, time) => {
        // Slow motion replay lasts for 0.5 seconds before and 0.5 seconds after
        endTime.current = time + 0.5;

        videoPlayer.current
            .setCurrentTime(time - 0.5)
            .then(() => {
                if (
                    activeDrawingScoreDefinition &&
                    activeDrawingScoreDefinition !== null
                ) {
                    setActiveDrawingScoreDefinition(null);
                }

                // Set the active step (step in our case is the scored scene)
                setActiveStep(step);
                stepRef.current = step;
            })
            .then((seconds) => {
                // Send slowmotion
                return videoPlayer.current.setPlaybackRate(0.3);
            })
            .then((playbackRate) => {
                // Start replay
                if (currentVideo.feedbackMode === "lab") {
                    stepRef.current = stepRef.current + 1;
                }
                return videoPlayer.current.play();
            })
            .catch((error) => {
                console.log(error);
            });
    };

    useEffect(() => {
        const player = document.getElementById("video-player");
        videoPlayer.current = new Player(player);

        videoPlayer.current.on("timeupdate", (data) => {
            console.log(data.seconds);
            setVideoProgress(data.seconds);
        });

        videoPlayer.current.on("ended", function () {
            videoEndRef.current = true;
            if (currentVideo.trailingPeriod > 0) {
                setIsOvertime(true);
                setOvertimeStartTime(Date.now());
            } else {
                setVideoEnd(true);

                savePlayerInputs(
                    playerDecisions.current,
                    currentVideo.courseId,
                    currentVideo.playlistId,
                    currentVideo.key,
                    currentVideo.feedbackMode,
                    currentVideo.sessionProperties.sessionMode,
                    currentVideo.isReplay
                );
            }
        });
    }, []);

    useEffect(() => {
        if (videoProgress >= currentVideo.sceneEnd - 0.25) {
            videoPlayer.current.pause().then(() => {
                if (currentVideo.trailingPeriod > 0) {
                    setIsOvertime(true);
                    setOvertimeStartTime(Date.now());
                } else {
                    savePlayerInputs(
                        playerDecisions.current,
                        currentVideo.courseId,
                        currentVideo.playlistId,
                        currentVideo.key,
                        currentVideo.feedbackMode,
                        currentVideo.sessionProperties.sessionMode,
                        currentVideo.isReplay
                    );

                    videoEndRef.current = true;
                    setVideoEnd(true);
                }
            });
        }
    }, [videoProgress]);

    useEffect(() => {
        if (
            videoProgress >= currentVideo.blurring?.visibilityStart &&
            videoProgress <= currentVideo.blurring?.visibilityEnd &&
            !videoEndRef.current
        ) {
            setShowBlurring(true);
        } else {
            setShowBlurring(false);
        }
    }, [videoProgress]);

    useEffect(() => { }, [videoEndRef]);

    useEffect(() => {
        if (videoProgress >= endTime.current && videoEndRef.current) {
            videoPlayer.current.pause().then(() => {
                setVideoEnd(true);
                setIsOvertime(false);
            });
        }

        if (
            currentVideo.scoreDefinitions[stepRef.current] &&
            currentVideo.scoreDefinitions[stepRef.current].highlighting &&
            videoProgress >=
            currentVideo.scoreDefinitions[stepRef.current].highlighting
                .visibilityStart &&
            videoProgress <=
            currentVideo.scoreDefinitions[stepRef.current].highlighting
                .visibilityEnd &&
            videoEndRef.current
        ) {
            console.log("SHOW HIGHLIGHT");
            if (!activeDrawingScoreDefinition) {
                setActiveDrawingScoreDefinition(
                    currentVideo.scoreDefinitions[stepRef.current]
                );
            }
        }

        if (
            currentVideo.scoreDefinitions[stepRef.current] &&
            currentVideo.scoreDefinitions[stepRef.current].highlighting &&
            videoProgress >
            currentVideo.scoreDefinitions[stepRef.current].highlighting
                .visibilityEnd &&
            videoEndRef.current
        ) {
            console.log("RESET CANVAS");
            if (activeDrawingScoreDefinition) {
                setActiveDrawingScoreDefinition(null);
            }
        }
    }, [videoProgress]);

    useEffect(() => {
        videoPlayer.current.getVideoHeight().then((height) => {
            setVideoHeight(height);
        });
        videoPlayer.current.getVideoWidth().then((width) => {
            setVideoWidth(width);
        });
    }, []);

    useEffect(() => {
        if (
            currentVideo.feedbackMode === "standard" &&
            videoEnd === true &&
            currentVideo.scoreDefinitions.length > 0
        ) {
            handleReplayScene(0, currentVideo.scoreDefinitions[0].time);
        }
    }, [videoEnd]);
    useEffect(() => {
        // In the case of a lab session, the scored scene replay should take place automatically
        // and only be done once

        if (
            currentVideo.feedbackMode === "lab" &&
            videoEnd === true &&
            stepRef.current < currentVideo.scoreDefinitions.length
        ) {
            handleReplayScene(
                stepRef.current,
                currentVideo.scoreDefinitions[stepRef.current].time
            );
            stepRef.current = stepRef.current + 1;
            console.log(stepRef.current);
            videoPlayer.current.on("pause", () => {
                handleReplayScene(
                    stepRef.current,
                    currentVideo.scoreDefinitions[stepRef.current].time
                );
                stepRef.current = stepRef.current + 1;
                console.log(stepRef.current);
            });
        }
    }, [videoEnd]);

    useEffect(() => {
        recalculateCanvasDimensions();
        window.addEventListener("resize", (event) => {
            recalculateCanvasDimensions();
        });
    }, [videoPlaying]);

    // Function that handles what happens when clicking play at the start of the video
    function handleClickPlay() {
        setDisabled(false);
        videoPlayer.current.setCurrentTime(currentVideo.sceneStart);
        videoPlayer.current.play().then(() => {
            if (!videoEndRef.current) {
                setVideoPlaying(true);
            }
        });
    }

    /*
         Functions to increment the score/penalties/warnings for red/blue player on button click
      */

    function handleDecisionRed(addedPoints) {
        setPointScoreState({
            ...pointScoreState,
            scoreRedPlayer: pointScoreState.scoreRedPlayer + addedPoints,
        });
        videoPlayer.current.getCurrentTime().then(function (seconds) {
            playerDecisions.current = [
                ...playerDecisions.current,
                {
                    time: isOvertime
                        ? seconds + (Date.now() - overtimeStartTime) / 1000
                        : seconds,
                    points: `${addedPoints}`,
                    athlete: "red",
                },
            ];
        });
    }

    function handleDecisionBlue(addedPoints) {
        console.log(addedPoints);
        setPointScoreState({
            ...pointScoreState,
            scoreBluePlayer: pointScoreState.scoreBluePlayer + addedPoints,
        });
        videoPlayer.current.getCurrentTime().then(function (seconds) {
            playerDecisions.current = [
                ...playerDecisions.current,
                {
                    time: isOvertime
                        ? seconds + (Date.now() - overtimeStartTime) / 1000
                        : seconds,
                    points: `${addedPoints}`,
                    athlete: "blue",
                },
            ];
        });
    }

    function handlePenaltyRed(penaltyId, penaltyValue) {
        setPenaltyRedScoreState({
            ...penaltyRedScoreState,
            [`${penaltyId}`]: penaltyRedScoreState[penaltyId] + penaltyValue,
        });
        videoPlayer.current.getCurrentTime().then(function (seconds) {
            playerDecisions.current = [
                ...playerDecisions.current,
                {
                    time: isOvertime
                        ? seconds + (Date.now() - overtimeStartTime) / 1000
                        : seconds,
                    points: `${penaltyId}`,
                    athlete: "red",
                },
            ];
        });
    }

    function handlePenaltyBlue(penaltyId, penaltyValue) {
        setPenaltyBlueScoreState({
            ...penaltyBlueScoreState,
            [`${penaltyId}`]: penaltyBlueScoreState[penaltyId] + penaltyValue,
        });
        videoPlayer.current.getCurrentTime().then(function (seconds) {
            playerDecisions.current = [
                ...playerDecisions.current,
                {
                    time: isOvertime
                        ? seconds + (Date.now() - overtimeStartTime) / 1000
                        : seconds,
                    points: `${penaltyId}`,
                    athlete: "blue",
                },
            ];
        });
    }

    function disableOnClick() { }

    const decisionHandlers = {
        incrementScoreRed: handleDecisionRed,
        incrementScoreBlue: handleDecisionBlue,
        incrementPenaltyRed: handlePenaltyRed,
        incrementPenaltyBlue: handlePenaltyBlue,
    };

    const recalculateCanvasDimensions = () => {
        const videoPlayer = document.getElementById("player-iframe-container");

        if (!videoPlayer) {
            return;
        }

        const wFrame = videoPlayer.getBoundingClientRect().width;
        const tFrame = videoPlayer.getBoundingClientRect().top;
        const bFrame = videoPlayer.getBoundingClientRect().bottom;

        setCanvasWidth(wFrame);
        setCanvasHeight((videoHeight / videoWidth) * wFrame);
        setCanvasLeft(0);
        setCanvasTop((bFrame - tFrame - (videoHeight / videoWidth) * wFrame) / 2);
    };

    const handleEndOvertime = () => {
        setIsOvertime(false);
        savePlayerInputs(
            playerDecisions.current,
            currentVideo.courseId,
            currentVideo.playlistId,
            currentVideo.key,
            currentVideo.feedbackMode,
            currentVideo.sessionProperties.sessionMode,
            currentVideo.isReplay
        );
        videoEndRef.current = true;
        setVideoEnd(true);
    };

    console.log(playerDecisions.current);
    return {
        // Session related states

        pointScoreState,
        penaltyRedScoreState,
        penaltyBlueScoreState,
        activeStep,
        isOvertime,
        disabled,
        videoEnd,
        reviewEnd,
        loadingFeedback,
        feedbackResult,
        videoPlaying,
        overtimeDuration,

        // Methods and other useful stuff

        decisionHandlers,
        handleClickPlay,
        handleReplayScene,
        handleChangeActiveDecision,
        checkRingOrTatami,
        disableOnClick,
        handleEndOvertime,

        // Stuff for rendering canvas
        activeDrawingScoreDefinition,
        canvasWidth,
        canvasHeight,
        canvasLeft,
        canvasTop,
        videoHeight,
        videoWidth,
        showBlurring,
    };
};

export default useGameSession;
